import { Checks, Copy, WarningCircle, X } from "@phosphor-icons/react";
import React, { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import dayjs from "dayjs"; // Use for date/time formatting
import PageLoader from "../loader/PageLoader";
import { CompleteHotel } from "./CompleteAPI";
import PromoBreakdown from "./PromoBreakdown";
import { formatHotel } from "../../api/hotelTime";

const EarlyCheckin = ({ func, selectedRequest }) => {

    const hotelId = localStorage.getItem("hotelId");

    const [remarks, setRemarks] = useState("");
    const [remarksError, setRemarksError] = useState(false);

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [data, setData] = useState({});
    const [guestRequestId, setGuestRequestId] = useState("");
    const [copied, setCopied] = useState(false);

    // Booking Name and Booking Contact Information
    const [bookingData,setBookingData] = useState({});
    const [roomPosition, setRoomPosition] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get(
                    `/api/v1/hotels/${hotelId}/bookings/${selectedRequest.bookingId}/booked-rooms/${selectedRequest.bookedRoomId}/guest-requests/pre-checkin/early-checkin-request`,
                    { params: { guest_request_id: selectedRequest.requestId } },
                );

                const bookingResponse = await axiosInstance.get(`/api/v1/hotels/${hotelId}/bookings/${selectedRequest.bookingId}`);
                setBookingData(bookingResponse.data);

                // Same sort the guest app uses, so "Room N" here is the same
                // room the guest picked when raising the request.
                const roomsResponse = await axiosInstance.get(
                    `/api/v1/hotels/${hotelId}/bookings/${selectedRequest.bookingId}/booked-rooms`,
                );
                const rooms = roomsResponse?.data?.data || [];
                const sortedRooms = [...rooms].sort((a, b) =>
                    String(a.id).localeCompare(String(b.id)),
                );
                const index = sortedRooms.findIndex(
                    (room) => room.id === selectedRequest.bookedRoomId,
                );
                setRoomPosition(index === -1 ? null : index + 1);

                setData(response.data);
                setGuestRequestId(response.data?.guest_request_id);
                setRemarks(response.data?.remarks || "");
                setErrors({});
            } catch (error) {
                console.log(error)
                setErrors({ fetch: "Failed to fetch Early check-in data" });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [selectedRequest, hotelId]);

    const isPendingHotel = data?.status === "Pending_Hotel";
    const isCompletable =
        data?.status === "Scheduled_Hotel" ||
        data?.status === "InProgress_Hotel";

    const handleVerdict = async (isDeclined) => {
        if (isDeclined && remarks.trim() === "") {
            setRemarksError(true);
            return;
        }

        const updatedData = {
            is_declined: isDeclined,
            remarks: remarks,
        };

        try {
            await axiosInstance.put(
                `/api/v1/hotels/${selectedRequest.hotelId}/bookings/${selectedRequest.bookingId}/booked-rooms/${selectedRequest.bookedRoomId}/guest-requests/pre-checkin/early-checkin-request/${data?.id}`,
                updatedData,
            );
            func(false);
        } catch (error) {
            console.error("error occurred", error);
            setErrors({ update: error?.response?.data?.message || "Failed to update Early check-in data" });
        }
    };

    const checkinDate = formatHotel(data?.proposed_checkin_time, "DD MMMM YYYY");
    const checkinTime = formatHotel(data?.proposed_checkin_time, "hh:mm A");

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000); // Reset copied state after 2 seconds
        });
    };

    const handleCompleteRequest = async () => {
        try {
            const request = {
                hotelId: selectedRequest.hotelId,
                bookingId: selectedRequest.bookingId,
                guestRequestId: guestRequestId,
            };
            await CompleteHotel(request);
            func(false);
        } catch (error) {
            setErrors({ update: "Failed to complete the early check-in request" });
            console.error(error);
        }
    };

    return (
        <div className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-60 flex items-center justify-center z-[1001]">
            {loading && <PageLoader />}
            <form
                className="popup-content bg-white p-6 rounded-lg text-sm flex flex-col gap-4 w-[80%] md:w-[70%] lg:w-[50%]"
                onSubmit={(e) => e.preventDefault()}
            >
                {/* Header */}
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">Early Check-In</h2>
                    <button
                        onClick={() => func(false)}
                        className="hover:bg-[#F2F7FF] p-1.5 border-[1.5px] rounded-md"
                        type="button"
                    >
                        <X weight="bold" size={18} />
                    </button>
                </div>

                {/* Booking Details */}
                <div className="grid grid-cols-4 gap-4">
                    <div className="min-w-0">
                        <p className="text-[#8C9199] mb-2">Booking ID</p>
                        <div className="flex items-center gap-1.5">
                            <p
                                className="truncate"
                                title={selectedRequest.bookingId}
                            >
                                {selectedRequest.bookingId}
                            </p>
                            <button
                                onClick={() =>
                                    handleCopy(selectedRequest.bookingId)
                                }
                                className="shrink-0 text-gray-500 hover:text-blue-500 flex items-center"
                                title="Copy to clipboard"
                                type="button"
                            >
                                {copied ? (
                                    <Checks size={16} />
                                ) : (
                                    <Copy size={16} />
                                )}
                            </button>
                        </div>
                    </div>
                    <div className="min-w-0">
                        <p className="text-[#8C9199] mb-2">Booked Room ID</p>
                        <p
                            className="truncate"
                            title={selectedRequest.bookedRoomId}
                        >
                            {selectedRequest.bookedRoomId}
                        </p>
                    </div>
                    <div className="min-w-0">
                        <p className="text-[#8C9199] mb-2">Check-in Date</p>
                        <p className="truncate">{checkinDate}</p>
                    </div>
                    <div className="min-w-0">
                        <p className="text-[#8C9199] mb-2">Check-in Time</p>
                        <p className="truncate">{checkinTime}</p>
                    </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                    <div className="min-w-0">
                        <p className="text-[#8C9199] mb-2">Booking Name</p>
                        <p
                            className="truncate"
                            title={bookingData?.name || 'Booking Name'}
                        >
                            {bookingData?.name || 'Booking Name'}
                        </p>
                    </div>
                    <div className="min-w-0">
                        <p className="text-[#8C9199] mb-2">Booking Contact</p>
                        <p
                            className="truncate"
                            title={bookingData?.contact || 'Booking Contact'}
                        >
                            +{bookingData?.contact || 'Booking Contact'}
                        </p>
                    </div>
                    <div className="min-w-0">
                        <p className="text-[#8C9199] mb-2">Hours Early</p>
                        <p className="truncate">
                            {data?.hours_early || 0} {data?.hours_early === 1 ? "hour" : "hours"}
                        </p>
                    </div>
                    <div className="min-w-0">
                        <p className="text-[#8C9199] mb-2">Room</p>
                        <p className="truncate">
                            {roomPosition ? `Room ${roomPosition}` : "-"}
                            {selectedRequest.roomNumber
                                ? ` · No. ${selectedRequest.roomNumber}`
                                : ""}
                        </p>
                    </div>
                </div>

                {/* Fee */}
                <div className="flex flex-col gap-4">
                    <div className="w-full bg-[#F2F7FF] rounded-md p-3 flex flex-col gap-1">
                        <div className="flex justify-between">
                            <p className="text-[#8C9199]">
                                {data?.hours_early || 0} × ₹{data?.hourly_rate || 0} per hour
                            </p>
                            <p>₹{(data?.hours_early || 0) * (data?.hourly_rate || 0)}</p>
                        </div>
                        <div className="flex justify-between font-semibold">
                            <p>Early check-in fee</p>
                            <p>₹{data?.price ?? 0}</p>
                        </div>
                    </div>

                    <PromoBreakdown
                        grossAmount={data?.price}
                        taxAmount={data?.tax_amount}
                        discountAmount={data?.discount_amount}
                        promotionId={data?.promotion_id}
                    />

                    <div>
                        <p className="text-[#8C9199] font-semibold">Remarks</p>
                        <textarea name="remarks" className="px-2 py-1.5 rounded-md border-[1.5px] mt-1 w-full resize-none"
                            onChange={(e)=>{
                                const value = e.target.value;
                                setRemarks(value);
                                setRemarksError(false);
                            }}
                            value={remarks}
                            rows={4}
                            placeholder="Please leave your remarks here"
                        ></textarea>
                        {remarksError && (
                            <p className="text-[#ff7378] flex gap-1 items-center text-xs mt-1">
                                <WarningCircle size={14} />
                                remarks is required when you deny the request
                            </p>
                        )}
                    </div>


                </div>

                {/* Action Buttons */}

                {isPendingHotel && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                        <button
                            className="p-2 w-full border-[1.5px] rounded-md border-[#F5CCD3] text-[#D02746] font-medium hover:bg-red-100"
                            onClick={() => handleVerdict(true)}
                            type="button"
                        >
                            Deny
                        </button>
                        <button
                            className="p-2 w-full rounded-md bg-[#ff7378] font-medium text-[#262B33] hover:bg-[#f9555a] outline-none"
                            onClick={() => handleVerdict(false)}
                            type="button"
                        >
                            Approve
                        </button>
                    </div>
                )}

                {isCompletable && (
                    <div className="mt-3">
                        <button
                            className="p-2 w-full rounded-md bg-[#73c9ff] font-medium text-[#262B33] hover:bg-[#5fa4f7] outline-none"
                            type="button"
                            onClick={handleCompleteRequest}
                        >
                            Complete Request
                        </button>
                    </div>
                )}


                {errors.fetch && <p className="text-red-500">{errors.fetch}</p>}
                {errors.update && (
                    <p className="text-red-500">{errors.update}</p>
                )}

            </form>
        </div>
    );
};

export default EarlyCheckin;
